import { TsRestClient } from '@eluve/api-client-provider';
import { SessionUnavailableError } from '@eluve/eluve-errors';
import { Logger } from '@eluve/logger-main';
import { getMetadataFromFileName } from '@eluve/utils';

import { MarkChunkFinalEvent, TaskHandler } from '../types';

export const markChunkFinalHandler = ({
  client,
  logger,
}: {
  client: TsRestClient | null;
  logger: Logger;
}): TaskHandler<MarkChunkFinalEvent> => {
  return async (task, context) => {
    const { databaseFileId: transcriptId } = getMetadataFromFileName({
      userId: task.userId,
      fileName: task.fileName,
    });

    if (!client) {
      logger.warn('API client is not available');
      throw new SessionUnavailableError('API client not initialized');
    }

    const response = await client.transcription.markChunkFinal({
      body: {
        appointmentId: task.appointmentId,
        transcriptId,
        isFinalChunk: true,
      },
      params: { tenantId: context.tenantId },
      fetchOptions: { signal: context.signal },
    });

    if (response?.status === 401) {
      throw new SessionUnavailableError(
        'Session expired while marking chunk final',
      );
    }

    if (response?.status === 403) {
      logger.warn('Appointment not accessible while marking chunk final', {
        appointmentId: task.appointmentId,
        fileName: task.fileName,
      });
      return;
    }

    // A 404 means the transcript record does not exist server-side (e.g. a
    // stale task for an appointment whose records were never persisted or were
    // cleaned up). Retrying cannot recover it, so give up terminally instead of
    // burning the full retry budget.
    if (response?.status === 404) {
      logger.warn('Transcript not found while marking chunk final, giving up', {
        appointmentId: task.appointmentId,
        fileName: task.fileName,
      });
      return;
    }

    if (response?.status !== 200) {
      logger.warn('Failed to mark chunk final', {
        fileName: task.fileName,
        status: response?.status,
      });

      throw new Error('Failed to finalize audio record');
    }
  };
};
